import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { DomainNotificationTemplateProviderContext } from '../../internal'
import { NotificationDto } from '../../../../features/notifications'
import { NotificationNamedTemplate } from '@pushly/models/lib/structs'

type LoadedNotificationTemplateProviderApi = {
    isLoading(): boolean
    isLoaded(): boolean
    isEmpty(): boolean
    hasRequestedTemplateId(): boolean
    getRequestedTemplateId(): string | null
    getTemplate(): NotificationNamedTemplate | null
    getNotifications(): NotificationDto[]
    getNotificationsPromise(): Promise<NotificationDto[]>
}

export function useLoadedDomainNotificationTemplate(): LoadedNotificationTemplateProviderApi {
    const ctx = useContext(DomainNotificationTemplateProviderContext)
    if (!ctx) {
        throw new Error('useLoadedDomainNotificationTemplate must be used within a DomainNotificationTemplateProvider')
    }

    const { state } = ctx

    const getNotifications = useCallback(() => {
        return state.notifications
    }, [state.domainId, state.templateId, state.notifications])

    // workaround for existing use with PromisableProp
    const getNotificationsRef = useRef(getNotifications)
    useEffect(() => {
        getNotificationsRef.current = getNotifications
    }, [getNotifications])

    const loadingRef = useRef(state.loading)
    useEffect(() => {
        loadingRef.current = state.loading
    }, [state.loading])

    const getNotificationsPromiseHasResponded = useRef(false)
    const getNotificationsPromise = useCallback(() => {
        return new Promise<NotificationDto[]>((res) => {
            const fallbackTimer = setTimeout(() => {
                if (getNotificationsPromiseHasResponded.current) return

                console.warn('getNotificationsPromise timed out')

                getNotificationsPromiseHasResponded.current = true
                clearTimeout(fallbackTimer)
                res([])
            }, 30_0000) // max API Gateway response time

            const _getNotifications = () => {
                if (getNotificationsPromiseHasResponded.current) {
                    return
                }

                if (loadingRef.current) {
                    setTimeout(_getNotifications, 100)
                    return
                }

                getNotificationsPromiseHasResponded.current = true
                loadingRef.current = false
                clearTimeout(fallbackTimer)
                res(getNotificationsRef.current())
            }

            _getNotifications()
        })
    }, [])

    const getTemplate = useCallback(() => state.template, [state.templateId, state.template])

    const isLoaded = useCallback(() => !state.loading, [state.loading])
    const isLoading = useCallback(() => state.loading, [state.loading])
    const isEmpty = useCallback(() => !state.notifications, [state.notifications])
    const hasRequestedTemplateId = useCallback(() => !!state.templateId, [state.templateId])
    const getRequestedTemplateId = useCallback(() => state.templateId, [state.templateId])

    return useMemo(
        () => ({
            isLoaded,
            isLoading,
            isEmpty,
            hasRequestedTemplateId,
            getRequestedTemplateId,
            getTemplate,
            getNotifications,
            getNotificationsPromise,
        }),
        [
            isLoaded,
            isLoading,
            isEmpty,
            hasRequestedTemplateId,
            getRequestedTemplateId,
            getTemplate,
            getNotifications,
            getNotificationsPromise,
        ],
    )
}
